import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { api } from './client';
import type { EventCard, EventListItem, Season, SessionUser } from './types';

/**
 * Серверное состояние целиком живёт здесь.
 *
 * Карточка мероприятия читается одним запросом и содержит всё:
 * номинации, услуги, программу, пакеты, браслеты, дедлайны, план,
 * вычисляемые значения и двенадцать проверок. Любая правка
 * инвалидирует этот ключ — и экран перерисовывается целиком,
 * ровно как render() в макете. Ручной синхронизации нет, потому
 * что именно она и расходится с сервером.
 */

export const keys = {
  me: ['me'] as const,
  seasons: ['seasons'] as const,
  events: (seasonId?: string) => ['events', seasonId ?? 'all'] as const,
  event: (id: string) => ['event', id] as const,
};

// ─── чтение ───

export const useMe = () =>
  useQuery({
    queryKey: keys.me,
    queryFn: () => api.get<SessionUser>('/auth/me'),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

export const useSeasons = () =>
  useQuery({ queryKey: keys.seasons, queryFn: () => api.get<Season[]>('/seasons') });

export const useEvents = (seasonId?: string) =>
  useQuery({
    queryKey: keys.events(seasonId),
    queryFn: () =>
      api.get<EventListItem[]>(`/events${seasonId ? `?season=${seasonId}` : ''}`),
  });

export const useEvent = (id: string | undefined) =>
  useQuery({
    queryKey: keys.event(id ?? ''),
    queryFn: () => api.get<EventCard>(`/events/${id}`),
    enabled: Boolean(id),
  });

// ─── правка ───

/**
 * Мутация, после которой мероприятие перечитывается целиком.
 * Отдельного «оптимистичного обновления» нет намеренно: половина
 * правил живёт на сервере, и локально угаданное состояние
 * рано или поздно разойдётся с настоящим.
 */
function useEventMutation<TArgs, TResult>(
  eventId: string,
  fn: (args: TArgs) => Promise<TResult>,
  options?: Omit<UseMutationOptions<TResult, Error, TArgs>, 'mutationFn'>,
) {
  const client = useQueryClient();

  return useMutation<TResult, Error, TArgs>({
    mutationFn: fn,
    ...options,
    onSuccess: async (data, vars, ctx, mutation) => {
      await client.invalidateQueries({ queryKey: keys.event(eventId) });
      await client.invalidateQueries({ queryKey: ['events'] });
      await options?.onSuccess?.(data, vars, ctx, mutation);
    },
  });
}

// мероприятие
export const useUpdateEvent = (id: string) =>
  useEventMutation(id, (body: Record<string, unknown>) =>
    api.patch<EventCard>(`/events/${id}`, body),
  );

export const usePublishEvent = (id: string) =>
  useEventMutation(id, () => api.post<EventCard>(`/events/${id}/publish`));

export const useSetAccepting = (id: string) =>
  useEventMutation(id, (accepting: boolean) =>
    api.post<EventCard>(`/events/${id}/accepting`, { accepting }),
  );

export const useUploadPoster = (id: string) =>
  useEventMutation(id, (file: File) => api.upload<EventCard>(`/events/${id}/poster`, file));

export const useUploadRegulation = (id: string) =>
  useEventMutation(id, (file: File) => api.upload<EventCard>(`/events/${id}/regulation`, file));

// номинации
export const useCreateNomination = (eventId: string) =>
  useEventMutation(eventId, (body: Record<string, unknown>) =>
    api.post(`/events/${eventId}/nominations`, body),
  );

export const useUpdateNomination = (eventId: string) =>
  useEventMutation(eventId, ({ id, ...body }: { id: string } & Record<string, unknown>) =>
    api.patch(`/nominations/${id}`, body),
  );

export const useDeleteNomination = (eventId: string) =>
  useEventMutation(eventId, (id: string) => api.delete(`/nominations/${id}`));

// услуги
export const useCreateService = (eventId: string) =>
  useEventMutation(eventId, (body: Record<string, unknown>) =>
    api.post(`/events/${eventId}/services`, body),
  );

export const useUpdateService = (eventId: string) =>
  useEventMutation(eventId, ({ id, ...body }: { id: string } & Record<string, unknown>) =>
    api.patch(`/services/${id}`, body),
  );

export const useDeleteService = (eventId: string) =>
  useEventMutation(eventId, (id: string) => api.delete(`/services/${id}`));

// программа
export const useCreateBlock = (eventId: string) =>
  useEventMutation(eventId, (body: Record<string, unknown>) =>
    api.post(`/events/${eventId}/blocks`, body),
  );

export const useUpdateBlock = (eventId: string) =>
  useEventMutation(eventId, ({ id, ...body }: { id: string } & Record<string, unknown>) =>
    api.patch(`/blocks/${id}`, body),
  );

export const useDeleteBlock = (eventId: string) =>
  useEventMutation(eventId, (id: string) =>
    api.delete<{ freedNominations: number; orphanedTickets: number }>(`/blocks/${id}`),
  );

export const useAddProgramItems = (eventId: string) =>
  useEventMutation(
    eventId,
    ({ blockId, items }: { blockId: string; items: { kind: 'nomination' | 'service'; refId: string }[] }) =>
      api.post(`/blocks/${blockId}/items`, { items }),
  );

export const useRemoveProgramItem = (eventId: string) =>
  useEventMutation(eventId, (itemId: string) => api.delete(`/block-items/${itemId}`));

export const useReorderProgram = (eventId: string) =>
  useEventMutation(eventId, ({ blockId, itemIds }: { blockId: string; itemIds: string[] }) =>
    api.patch(`/blocks/${blockId}/items/order`, { itemIds }),
  );

// пакеты
export const useCreatePackage = (eventId: string) =>
  useEventMutation(eventId, (body: Record<string, unknown>) =>
    api.post<{ id: string }>(`/events/${eventId}/packages`, body),
  );

export const useUpdatePackage = (eventId: string) =>
  useEventMutation(eventId, ({ id, ...body }: { id: string } & Record<string, unknown>) =>
    api.patch(`/packages/${id}`, body),
  );

export const useDeletePackage = (eventId: string) =>
  useEventMutation(eventId, (id: string) => api.delete(`/packages/${id}`));

export const useSetPackageState = (eventId: string) =>
  useEventMutation(eventId, ({ id, state }: { id: string; state: string }) =>
    api.post(`/packages/${id}/state`, { state }),
  );

export const useSetPackageServices = (eventId: string) =>
  useEventMutation(eventId, ({ id, serviceIds }: { id: string; serviceIds: string[] }) =>
    api.put(`/packages/${id}/services`, { serviceIds }),
  );

export const useSetPackageNominations = (eventId: string) =>
  useEventMutation(
    eventId,
    ({ id, nominations }: { id: string; nominations: { nominationId: string; isDefault: boolean }[] }) =>
      api.put(`/packages/${id}/nominations`, { nominations }),
  );

// браслеты
export const useCreateBand = (eventId: string) =>
  useEventMutation(eventId, (body: Record<string, unknown>) =>
    api.post(`/events/${eventId}/bands`, body),
  );

export const useUpdateBand = (eventId: string) =>
  useEventMutation(eventId, ({ id, ...body }: { id: string } & Record<string, unknown>) =>
    api.patch(`/bands/${id}`, body),
  );

export const useDeleteBand = (eventId: string) =>
  useEventMutation(eventId, (id: string) => api.delete(`/bands/${id}`));

// дедлайны
export const useCreateDeadline = (eventId: string) =>
  useEventMutation(eventId, (body: Record<string, unknown>) =>
    api.post(`/events/${eventId}/deadlines`, body),
  );

export const useUpdateDeadline = (eventId: string) =>
  useEventMutation(eventId, ({ id, ...body }: { id: string } & Record<string, unknown>) =>
    api.patch(`/deadlines/${id}`, body),
  );

export const useDeleteDeadline = (eventId: string) =>
  useEventMutation(eventId, (id: string) => api.delete(`/deadlines/${id}`));

// план
export const useUpdatePlan = (eventId: string) =>
  useEventMutation(eventId, (body: Record<string, unknown>) =>
    api.put(`/events/${eventId}/plan`, body),
  );

// создание и копирование
export const useCreateEvent = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<EventCard>('/events', body),
    onSuccess: () => client.invalidateQueries({ queryKey: ['events'] }),
  });
};

export const useCopyEvent = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ sourceId, ...body }: { sourceId: string } & Record<string, unknown>) =>
      api.post<EventCard>(`/events/${sourceId}/copy`, body),
    onSuccess: () => client.invalidateQueries({ queryKey: ['events'] }),
  });
};

export const useDeleteEvent = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/events/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: ['events'] }),
  });
};

// вход
export const useLogin = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; password: string }) =>
      api.post<SessionUser>('/auth/login', body),
    onSuccess: (user) => client.setQueryData(keys.me, user),
  });
};

export const useLogout = () =>
  useMutation({
    mutationFn: () => api.post('/auth/logout'),
    onSuccess: () => {
      /*
        Полная перезагрузка, а не очистка кэша.

        queryClient.clear() убирает записи из кэша, но не уведомляет
        подписчиков: App продолжает рисовать приложение с данными
        прежнего пользователя, а запросы один за другим получают 401.
        Со стороны это выглядит как «кнопка не работает».

        Перезагрузка — единственный способ гарантировать, что
        от прошлой сессии не осталось ничего: ни в кэше запросов,
        ни в состоянии компонентов. Для выхода это ровно то,
        что нужно.
      */
      window.location.assign('/');
    },
  });
